import { Button } from '@awell/ui-kit/components/Button'
import {
  HeadingSecondary
  // HeadingTertiary, // No longer needed
} from '@awell/ui-kit/components/Typography'
import React, { type FC, type ReactElement } from 'react'
import { t } from 'i18next'
import { usePatientPathways } from '../../hooks/usePatientPathways'
import { useStyles } from './useStyles'
import { PathwayStatus } from './types'
import { ActivePathwaysCardSkeleton } from './skeletons/ActivePathwaysCardSkeleton'
import { isNil } from 'lodash'
import { formatDate as formatDateUtils } from '../../utils'

interface ActivePathwaysCardProps {
  patientId: string
  onViewAll: () => void
  onViewPathway: (id: string) => void
}

const ACTIVE_PATHWAYS_COUNT = 3

const formatDate = (dateString?: string | null): string => {
  if (isNil(dateString)) {
    return '-'
  }
  return formatDateUtils(false)(dateString) ?? '-'
}

export const ActivePathwaysCard: FC<ActivePathwaysCardProps> = ({
  patientId,
  onViewAll,
  onViewPathway
}): ReactElement => {
  const classes = useStyles()

  const { patientPathways, loading } = usePatientPathways(patientId)

  if (loading) {
    return <ActivePathwaysCardSkeleton />
  }
  /* @TODO Sort by last activity date */
  const activePatientPathways = (patientPathways ?? [])
    .filter(pathway => pathway.status === PathwayStatus.Active || pathway.status === PathwayStatus.Starting)
    .splice(0, ACTIVE_PATHWAYS_COUNT)

  return (
    <section className={classes.activePathwaysContainer}>
      <HeadingSecondary variant="subHeadline">{t('active_pathways')}</HeadingSecondary>
      <div className={classes.activePathwaysList}>
        {activePatientPathways.map(pathway => {
          const startedBy = pathway.created?.user_name ?? pathway.created?.user_email ?? 'Enrollment trigger'
          const startedAt = formatDate(pathway.start_date)
          const lastActivityAt = formatDate(pathway.latest_activity_date)

          return (
            <section
              key={pathway.id}
              className={classes.pathwayRow}
              onClick={() => onViewPathway(pathway.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onViewPathway(pathway.id)
                }
              }}
            >
              <div className={classes.pathwayNameAndActivitiesNumberWrapper}>
                <span className={classes.pathwayTitleLink}>{pathway.title}</span>
                <p style={{ fontSize: '0.8rem', color: '#555', margin: '4px 0 0 0' }}>
                  Started: {startedAt} by {startedBy}
                </p>
                <p style={{ fontSize: '0.8rem', color: '#555', margin: '2px 0 0 0' }}>
                  Last activity: {lastActivityAt}
                </p>
              </div>
            </section>
          )
        })}
      </div>

      <Button onClick={onViewAll} className={classes.cta} color="primary">
        {t('view_all')}
      </Button>
    </section>
  )
}

ActivePathwaysCard.displayName = 'ActivePathwaysCard'
